import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  User, 
  Phone, 
  Mail,
  Eye,
  Edit,
  X,
  CheckCircle,
  AlertCircle,
  Plus,
  Filter,
  Search,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { RootState } from '../store';
import AccountLayout from '../components/AccountLayout';
import { ModalHeader } from '../components/ModalHeader';
import { clientAppointmentsApi, PiercingRecordDetail } from '../services/clientAppointmentsApi';
import { RichTextEditor } from '../components/RichTextEditor';
import {
  buildClientNotesPayload,
  ClientNoteKind,
  ClientNoteSection,
  getClientNoteSections,
  sanitizeRichTextHtml,
} from '@digitaltattoo/shared-utils';
import { useSystemStatus } from '../hooks/useSystemStatus';

interface Appointment {
  id: string;
  scheduledAt: string;
  estimatedEndTime: string;
  date: string;
  time: string;
  duration: number;
  service: string;
  artist: string;
  status: 'scheduled' | 'completed' | 'cancelled' | 'rescheduled';
  location: string;
  notes?: string;
  clientNotes?: string | null;
  price: number;
  depositAmount?: number | null;
  depositRequired?: boolean;
  type: string;
  checkInCode?: string | null;
  piercings?: Array<{ bodyZone?: string; piercingType?: string }>;
}

interface AppointmentFilters {
  status: string;
  dateRange: string;
  service: string;
}

export function AppointmentsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tattooBookingEnabled } = useSystemStatus();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<AppointmentFilters>({
    status: 'all',
    dateRange: 'all',
    service: 'all'
  });
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [noteDrafts, setNoteDrafts] = useState<ClientNoteSection[]>([]);
  const [noteEditMode, setNoteEditMode] = useState(false);
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [piercingRecordDetails, setPiercingRecordDetails] = useState<PiercingRecordDetail[]>([]);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate('/login');
      return;
    }
    
    loadAppointments();
  }, [isAuthenticated, user, navigate]);

  const loadAppointments = async () => {
    try {
      setLoading(true);
      const response = await clientAppointmentsApi.list();
      const apiAppointments = response.data?.appointments || [];

      const mapStatus = (status: string): Appointment['status'] => {
        switch (status?.toUpperCase()) {
          case 'SCHEDULED':
          case 'CONFIRMED':
          case 'CHECKED_IN':
          case 'IN_PROGRESS':
          case 'PENDING_PAYMENT':
            return 'scheduled';
          case 'COMPLETED':
            return 'completed';
          case 'CANCELLED':
          case 'NO_SHOW':
            return 'cancelled';
          case 'RESCHEDULED':
            return 'rescheduled';
          default:
            return 'scheduled';
        }
      };

      const mapped: Appointment[] = apiAppointments.map((apt: any) => {
        const start = new Date(apt.scheduledAt);
        const end = new Date(apt.estimatedEndTime);
        const durationMinutes = Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())
          ? 0
          : Math.max(0, Math.round((end.getTime() - start.getTime()) / (1000 * 60)));
        const price = apt.pricing?.total ?? apt.pricing?.subtotal ?? apt.pricing?.totalPrice ?? 0;
        const shopCity = apt.shopAddress?.city ? `, ${apt.shopAddress.city}` : '';
        const practitionerName = apt.practitionerArtistName
          || (apt.practitionerFirstName || apt.practitionerLastName
            ? `${apt.practitionerFirstName || ''} ${apt.practitionerLastName || ''}`.trim()
            : '-');
        return {
          id: apt.id,
          scheduledAt: apt.scheduledAt,
          estimatedEndTime: apt.estimatedEndTime,
          date: Number.isNaN(start.getTime()) ? '' : start.toISOString().split('T')[0],
          time: Number.isNaN(start.getTime())
            ? ''
            : start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
          duration: durationMinutes,
          service: apt.serviceName || apt.type || '',
          artist: practitionerName,
          status: mapStatus(apt.status),
          location: `${apt.shopName || ''}${shopCity}`.trim(),
          notes: apt.notes || '',
          clientNotes: apt.clientNotes ?? null,
          price,
          depositAmount: apt.depositAmount ?? apt.pricing?.depositAmount ?? apt.pricing?.deposit ?? null,
          depositRequired: Boolean(apt.depositRequired),
          type: apt.type || '',
          checkInCode: apt.checkInCode || null,
          piercings: Array.isArray(apt.piercings) ? apt.piercings : [],
        };
      });

      setAppointments(mapped);
    } catch (error) {
      console.error('Failed to load appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'rescheduled':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'scheduled':
        return Clock;
      case 'completed':
        return CheckCircle;
      case 'cancelled':
        return X;
      case 'rescheduled':
        return Edit;
      default:
        return Calendar;
    }
  };

  const filteredAppointments = appointments.filter(appointment => {
    const matchesSearch = appointment.service.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         appointment.artist.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filters.status === 'all' || appointment.status === filters.status;
    
    return matchesSearch && matchesStatus;
  });

  const upcomingAppointments = appointments.filter(apt => 
    apt.status === 'scheduled' && new Date(apt.date) >= new Date()
  );

  const pastAppointments = appointments.filter(apt => 
    apt.status === 'completed' || apt.status === 'cancelled' || 
    (apt.status === 'scheduled' && new Date(apt.date) < new Date())
  );

  const handleViewDetails = async (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setShowDetails(true);
    setPiercingRecordDetails([]);
    const kind: ClientNoteKind = appointment.type === 'PIERCING' ? 'piercing' : 'tattoo';
    const sections = getClientNoteSections(appointment.clientNotes, appointment.piercings, kind);
    const nextSections = sections.length > 0
      ? sections
      : (kind === 'piercing'
          ? getClientNoteSections(null, appointment.piercings, kind)
          : [{ kind, html: '' }]);
    setNoteDrafts(nextSections);
    // Fetch full appointment detail to get piercing records
    try {
      const detail = await clientAppointmentsApi.getDetail(appointment.id);
      if (detail.success && Array.isArray(detail.data?.piercingRecords)) {
        setPiercingRecordDetails(detail.data!.piercingRecords!);
      }
    } catch {
      // Non-blocking
    }
    setNoteEditMode(false);
    setNoteError(null);
  };

  const handleCancelAppointment = async (appointmentId: string) => {
    try {
      // TODO: Implement cancel appointment API call
      console.log('Cancelling appointment:', appointmentId);
      // Update local state
      setAppointments(prev => prev.map(apt => 
        apt.id === appointmentId ? { ...apt, status: 'cancelled' as const } : apt
      ));
    } catch (error) {
      console.error('Failed to cancel appointment:', error);
    }
  };

  const handleEditAppointment = (appointmentId: string) => {
    navigate(`/account/appointments/${appointmentId}`);
  };

  const resetNoteDraftsFromAppointment = (appointment: Appointment | null) => {
    if (!appointment) return;
    const kind: ClientNoteKind = appointment.type === 'PIERCING' ? 'piercing' : 'tattoo';
    const sections = getClientNoteSections(
      appointment.clientNotes,
      appointment.piercings,
      kind,
    );
    const resetSections = sections.length > 0
      ? sections
      : (kind === 'piercing'
          ? getClientNoteSections(null, appointment.piercings, kind)
          : [{ kind, html: '' }]);
    setNoteDrafts(resetSections);
  };

  const handleCloseDetails = () => {
    resetNoteDraftsFromAppointment(selectedAppointment);
    setShowDetails(false);
    setNoteEditMode(false);
    setNoteError(null);
  };

  const handleSaveNotes = async () => {
    if (!selectedAppointment) return;
    try {
      setNoteSaving(true);
      setNoteError(null);
      const kind: ClientNoteKind = selectedAppointment.type === 'PIERCING' ? 'piercing' : 'tattoo';
      const payload = buildClientNotesPayload(kind, noteDrafts);
      const response = await clientAppointmentsApi.updateNotes(selectedAppointment.id, payload);
      const updatedNotes = response.data?.appointments?.[0]?.clientNotes ?? response.data?.clientNotes ?? null;

      setAppointments(prev =>
        prev.map(apt => apt.id === selectedAppointment.id ? { ...apt, clientNotes: updatedNotes } : apt)
      );
      setSelectedAppointment(prev => prev ? { ...prev, clientNotes: updatedNotes } : prev);
      setNoteEditMode(false);
    } catch (error) {
      console.error('Failed to save appointment notes:', error);
      setNoteError(t('auth.account.appointments.noteSaveError'));
    } finally {
      setNoteSaving(false);
    }
  };

  const selectedNoteKind: ClientNoteKind | null = selectedAppointment
    ? (selectedAppointment.type === 'PIERCING' ? 'piercing' : 'tattoo')
    : null;
  const selectedNoteSections = selectedAppointment
    ? getClientNoteSections(selectedAppointment.clientNotes, selectedAppointment.piercings, selectedNoteKind || 'tattoo')
    : [];
  const hasSelectedNotes = selectedNoteSections.length > 0;

  if (loading) {
    return (
      <AccountLayout title={t('auth.account.appointments.title')}>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
            <p className="text-textSecondary">{t('auth.account.appointments.loading')}</p>
          </div>
        </div>
      </AccountLayout>
    );
  }

  return (
    <AccountLayout 
      title={t('auth.account.appointments.title')}
      subtitle={t('auth.account.appointments.subtitle')}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Calendar className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-textSecondary">
                  {t('auth.account.appointments.stats.total')}
                </p>
                <p className="text-2xl font-bold text-textPrimary">
                  {appointments.length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Clock className="h-8 w-8 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-textSecondary">
                  {t('auth.account.appointments.stats.upcoming')}
                </p>
                <p className="text-2xl font-bold text-textPrimary">
                  {upcomingAppointments.length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-textSecondary">
                  {t('auth.account.appointments.stats.completed')}
                </p>
                <p className="text-2xl font-bold text-textPrimary">
                  {appointments.filter(apt => apt.status === 'completed').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <X className="h-8 w-8 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-textSecondary">
                  {t('auth.account.appointments.stats.cancelled')}
                </p>
                <p className="text-2xl font-bold text-textPrimary">
                  {appointments.filter(apt => apt.status === 'cancelled').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions Bar */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
              {/* Search */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-textSecondary" />
                </div>
                <input
                  type="text"
                  placeholder={t('auth.account.appointments.search')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Filters Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-textSecondary hover:text-textPrimary hover:bg-gray-50 transition-colors"
              >
                <Filter className="h-4 w-4 mr-2" />
                {t('auth.account.appointments.filters')}
                {showFilters ? (
                  <ChevronUp className="h-4 w-4 ml-2" />
                ) : (
                  <ChevronDown className="h-4 w-4 ml-2" />
                )}
              </button>
            </div>

            {/* Book New Appointment */}
            <div className="flex items-center gap-2">
              {tattooBookingEnabled && (
                <button
                  onClick={() => navigate('/book-tattoo?fresh=1')}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-primary-500 to-accent-500 hover:from-primary-600 hover:to-accent-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('auth.account.appointments.bookTattoo', 'Tattoo')}
                </button>
              )}
              <button
                onClick={() => navigate('/book-piercing?fresh=1')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-primary-500 to-accent-500 hover:from-primary-600 hover:to-accent-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('auth.account.appointments.bookPiercing', 'Piercing')}
              </button>
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    {t('auth.account.appointments.filterStatus')}
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="all">{t('auth.account.appointments.allStatuses')}</option>
                    <option value="scheduled">{t('auth.account.appointments.scheduled')}</option>
                    <option value="completed">{t('auth.account.appointments.completed')}</option>
                    <option value="cancelled">{t('auth.account.appointments.cancelled')}</option>
                    <option value="rescheduled">{t('auth.account.appointments.rescheduled')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    {t('auth.account.appointments.filterDateRange')}
                  </label>
                  <select
                    value={filters.dateRange}
                    onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="all">{t('auth.account.appointments.allDates')}</option>
                    <option value="upcoming">{t('auth.account.appointments.upcoming')}</option>
                    <option value="past">{t('auth.account.appointments.past')}</option>
                    <option value="thisMonth">{t('auth.account.appointments.thisMonth')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    {t('auth.account.appointments.filterService')}
                  </label>
                  <select
                    value={filters.service}
                    onChange={(e) => setFilters(prev => ({ ...prev, service: e.target.value }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="all">{t('auth.account.appointments.allServices')}</option>
                    <option value="tattoo">{t('auth.account.appointments.tattoo')}</option>
                    <option value="piercing">{t('auth.account.appointments.piercing')}</option>
                    <option value="consultation">{t('auth.account.appointments.consultation')}</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Appointments List */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-textPrimary">
              {t('auth.account.appointments.appointmentsList')} ({filteredAppointments.length})
            </h3>
          </div>

          {filteredAppointments.length === 0 ? (
            <div className="p-12 text-center">
              <Calendar className="h-12 w-12 text-textSecondary mx-auto mb-4" />
              <h3 className="text-lg font-medium text-textPrimary mb-2">
                {t('auth.account.appointments.noAppointments')}
              </h3>
              <p className="text-textSecondary mb-6">
                {t('auth.account.appointments.noAppointmentsDesc')}
              </p>
              <div className="flex items-center gap-2 justify-center">
                {tattooBookingEnabled && (
                  <button
                    onClick={() => navigate('/book-tattoo?fresh=1')}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-primary-500 to-accent-500 hover:from-primary-600 hover:to-accent-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {t('auth.account.appointments.bookTattoo', 'Tattoo')}
                  </button>
                )}
                <button
                  onClick={() => navigate('/book-piercing?fresh=1')}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-primary-500 to-accent-500 hover:from-primary-600 hover:to-accent-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('auth.account.appointments.bookPiercing', 'Piercing')}
                </button>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredAppointments.map((appointment) => {
                const StatusIcon = getStatusIcon(appointment.status);
                return (
                  <div key={appointment.id} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 bg-gradient-to-br from-primary-300 to-accent-500 rounded-lg flex items-center justify-center">
                            <StatusIcon className="h-6 w-6 text-white" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="text-lg font-medium text-textPrimary truncate">
                              {appointment.service}
                            </h4>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(appointment.status)}`}>
                              {t(`auth.account.appointments.${appointment.status}`)}
                            </span>
                          </div>
                          <div className="flex items-center space-x-4 text-sm text-textSecondary">
                            <div className="flex items-center">
                              <Calendar className="h-4 w-4 mr-1" />
                              {new Date(appointment.date).toLocaleDateString()}
                            </div>
                            <div className="flex items-center">
                              <Clock className="h-4 w-4 mr-1" />
                              {appointment.time}
                            </div>
                            <div className="flex items-center">
                              <User className="h-4 w-4 mr-1" />
                              {appointment.artist}
                            </div>
                            <div className="flex items-center">
                              <MapPin className="h-4 w-4 mr-1" />
                              {appointment.location}
                            </div>
                            {appointment.checkInCode && (
                              <div className="flex items-center font-semibold tracking-[0.2em] text-textPrimary">
                                {appointment.checkInCode}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-lg font-semibold text-textPrimary">
                          {appointment.type === 'CONSULTATION'
                            ? t('onlineBooking.service.free', 'Gratuit')
                            : appointment.type === 'TATTOO_SESSION' || appointment.type === 'TATTOO'
                              ? (appointment.depositRequired && appointment.depositAmount
                                  ? `${t('appointmentDetail.deposit', 'Acompte')} €${appointment.depositAmount}`
                                  : t('appointmentDetail.onQuote', 'Sur devis'))
                              : `€${appointment.price}`}
                        </span>
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => handleViewDetails(appointment)}
                            className="p-2 text-textSecondary hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                            title={t('auth.account.appointments.viewDetails')}
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          {appointment.status === 'scheduled' && (
                            <>
                              <button
                                onClick={() => handleEditAppointment(appointment.id)}
                                className="p-2 text-textSecondary hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title={t('auth.account.appointments.editAppointment', 'Edit')}
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleCancelAppointment(appointment.id)}
                                className="p-2 text-textSecondary hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title={t('auth.account.appointments.cancel')}
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Appointment Details Modal */}
      {showDetails && selectedAppointment && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseDetails}></div>
            
            <div className="modal-panel inline-block align-bottom rounded-lg text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <ModalHeader
                  title={t('auth.account.appointments.appointmentDetails')}
                  onClose={handleCloseDetails}
                  className="mb-4"
                />
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-textPrimary">{selectedAppointment.service}</h4>
                    <p className="text-textSecondary">{selectedAppointment.artist}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-textSecondary">{t('auth.account.appointments.date')}</p>
                      <p className="text-textPrimary">{new Date(selectedAppointment.date).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-textSecondary">{t('auth.account.appointments.time')}</p>
                      <p className="text-textPrimary">{selectedAppointment.time}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-textSecondary">{t('auth.account.appointments.duration')}</p>
                      <p className="text-textPrimary">{selectedAppointment.duration} min</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-textSecondary">
                        {selectedAppointment.type === 'TATTOO_SESSION' || selectedAppointment.type === 'TATTOO'
                          ? t('appointmentDetail.deposit', 'Acompte')
                          : t('auth.account.appointments.price')}
                      </p>
                      <p className="text-textPrimary">
                        {selectedAppointment.type === 'CONSULTATION'
                          ? t('onlineBooking.service.free', 'Gratuit')
                          : selectedAppointment.type === 'TATTOO_SESSION' || selectedAppointment.type === 'TATTOO'
                            ? (selectedAppointment.depositRequired && selectedAppointment.depositAmount
                                ? `€${selectedAppointment.depositAmount}`
                                : t('appointmentDetail.onQuote', 'Sur devis'))
                            : `€${selectedAppointment.price}`}
                      </p>
                    </div>
                  </div>

                  {(selectedAppointment.type === 'TATTOO_SESSION' || selectedAppointment.type === 'TATTOO') && (
                    <p className="text-xs text-textSecondary">
                      {t('appointmentDetail.quoteNote', 'Le prix final sera établi sur devis le jour du rendez-vous.')}
                    </p>
                  )}
                  
                  <div>
                    <p className="text-sm font-medium text-textSecondary">{t('auth.account.appointments.location')}</p>
                    <p className="text-textPrimary">{selectedAppointment.location}</p>
                  </div>
                  
                  {selectedAppointment.notes && (
                    <div>
                      <p className="text-sm font-medium text-textSecondary">{t('auth.account.appointments.notes')}</p>
                      <p className="text-textPrimary">{selectedAppointment.notes}</p>
                    </div>
                  )}

                  <div>
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-textSecondary">
                        {t('auth.account.appointments.noteToPractitioner')}
                      </p>
                      <button
                        type="button"
                        onClick={() => setNoteEditMode(true)}
                        className="text-xs fo-link hover:no-underline"
                      >
                        {t('auth.account.appointments.editNote')}
                      </button>
                    </div>

                    {noteEditMode ? (
                      <div className="mt-3 space-y-4">
                        {noteDrafts.map((section, index) => (
                          <div key={`${section.title || 'note'}-${index}`}>
                            {section.title && (
                              <p className="text-xs font-medium text-textSecondary mb-2">{section.title}</p>
                            )}
                            <RichTextEditor
                              value={section.html}
                              onChange={(value) =>
                                setNoteDrafts((prev) =>
                                  prev.map((item, idx) => (idx === index ? { ...item, html: value } : item)),
                                )
                              }
                              placeholder={t('auth.account.appointments.notePlaceholder')}
                            />
                          </div>
                        ))}
                        {noteError && <p className="text-sm text-red-600">{noteError}</p>}
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={handleSaveNotes}
                            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
                            disabled={noteSaving}
                          >
                            {noteSaving
                              ? t('auth.account.appointments.savingNote')
                              : t('auth.account.appointments.saveNote')}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              resetNoteDraftsFromAppointment(selectedAppointment);
                              setNoteEditMode(false);
                              setNoteError(null);
                            }}
                            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                          >
                            {t('auth.account.appointments.cancelEdit')}
                          </button>
                        </div>
                      </div>
                    ) : hasSelectedNotes ? (
                      <div className="mt-3 space-y-3">
                        {selectedNoteSections.map((section, index) => (
                          <div key={`${section.title || 'note'}-${index}`} className="bg-blue-50 p-3 rounded-lg">
                            {section.title && (
                              <p className="text-xs font-medium text-blue-900 mb-2">{section.title}</p>
                            )}
                            <div
                              className="text-textPrimary text-sm max-w-none
                                [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-2
                                [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-2
                                [&_li]:my-1 [&_p]:my-1"
                              dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(section.html) }}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-textSecondary mt-2">
                        {t('auth.account.appointments.noNote')}
                      </p>
                    )}
                  </div>
                  
                  {piercingRecordDetails.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-textSecondary mb-2">Détails piercing</p>
                      <div className="space-y-3">
                        {piercingRecordDetails.map((pr) => {
                          const piercingTypeLabels: Record<string, string> = {
                            EAR_LOBE: "Lobe de l'oreille",
                            EAR_CARTILAGE: "Cartilage de l'oreille",
                            NOSE: 'Nez',
                            EYEBROW: 'Sourcil',
                            LIP: 'Lèvre',
                            TONGUE: 'Langue',
                            NAVEL: 'Nombril',
                            NIPPLE: 'Téton',
                            GENITAL: 'Génital',
                            SURFACE: 'Surface',
                            DERMAL: 'Dermal',
                          };
                          const label = piercingTypeLabels[pr.piercingType] || pr.piercingType;
                          const placement = [pr.placement?.area, pr.placement?.side, pr.placement?.specific].filter(Boolean).join(' — ');
                          return (
                            <div key={pr.id} className="bg-violet-50 border border-violet-200 rounded-lg p-3 text-sm space-y-1">
                              <p className="font-medium text-violet-800">{label}</p>
                              {placement && <p className="text-violet-700">📍 {placement}</p>}
                              {pr.jewelry?.type && (
                                <p className="text-violet-700">💍 {pr.jewelry.type} — {pr.jewelry.material} ({pr.jewelry.gauge})</p>
                              )}
                              {pr.needleGauge && <p className="text-violet-700">🪡 Aiguille : {pr.needleGauge}</p>}
                              <p className="text-violet-700">⏳ Guérison estimée : {pr.healingTime} semaines</p>
                              {pr.aftercareProvided?.products && (
                                <p className="text-violet-700">🧴 Produits : {pr.aftercareProvided.products}</p>
                              )}
                              {pr.aftercareProvided?.instructions && (
                                <p className="text-violet-700">📋 Consignes : {pr.aftercareProvided.instructions}</p>
                              )}
                              {pr.complications && pr.complications.length > 0 && (
                                <p className="text-orange-700">⚠️ Complications : {pr.complications.join(', ')}</p>
                              )}
                              {pr.isHealed && (
                                <p className="text-green-700 font-medium">✅ Guéri</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div>
                    <p className="text-sm font-medium text-textSecondary">{t('auth.account.appointments.status')}</p>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedAppointment.status)}`}>
                      {t(`auth.account.appointments.${selectedAppointment.status}`)}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:justify-end">
                <button
                  type="button"
                  onClick={handleCloseDetails}
                  className="btn-secondary w-full sm:w-auto"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AccountLayout>
  );
}

export default AppointmentsPage;
